import React from "react"
import autobind from "class-autobind"
import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from "material-ui/Table"
import strings from "../../../localization/strings"
import ImageLoader from "../../../components/misc/ImageLoader"
import PayrollByHierarchyEmployeeRowExportedColumn from "../../../components/payroll/PayrollByHierarchyEmployeeRowExportedColumn"
import { browserHistory } from "react-router"
import { IPayrollExpensesHierarchyEmployee, EApproveType, ILockedObjData, IPayrollLockedData } from "../../../models"
import ExpensesApproveIconTooltip from "../../../components/expense/ExpensesApproveIconTooltip"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import AppStateStore from "../../../stores/AppStateStore"
import * as api from "../../../api"
import * as utils from "../../../helpers/utils"
import UserDetailExpensesTable from "../../../components/expense/UserDetailExpensesTable"
import PayrollByHierarchyTableLockedColumn from "../../../components/payroll/PayrollByHierarchyTableLockedColumn"

interface IProps {
	employees: IPayrollExpensesHierarchyEmployee[]
	expandState: boolean
	showExported: boolean
	hierarchyIndex: number
	payrollHierarchyLockedData: IPayrollLockedData
	handleSelectEmployee(hierarchyIndex: number, selectedEmployees: IPayrollExpensesHierarchyEmployee[], unselectedEmployees: IPayrollExpensesHierarchyEmployee[]): void
	onExpensePayrollFileDownload?(expenseId: string): void
}
interface IState {
	expandRow: number
	selectedEmployees: IPayrollExpensesHierarchyEmployee[]
}

export default class PayrollExpensesByHierarchyEmployeeRow extends React.Component<IProps, IState> {
	constructor(props) {
		super(props)
		this.state = {
			expandRow: -1,
			selectedEmployees: []
		}
		autobind(this)
	}

	handleSelectEmployee(rows) {
		const selectedEmployees = []
		rows.forEach((row) => selectedEmployees.push(this.props.employees[row]))
		this.setState({ selectedEmployees })
		const unselectedEmployees = []
		this.props.employees.forEach((item) => {
			if (!selectedEmployees.find((selectedItem) => selectedItem.EmployeeId === item.EmployeeId)) {
				unselectedEmployees.push(item)
			}
		})
		this.props.handleSelectEmployee(this.props.hierarchyIndex, selectedEmployees, unselectedEmployees)
	}

	getMissingPunchCount(employeeData) {
		let missingPunchCount = 0
		employeeData.JobCostingSummaryList.forEach((dayData) => {
			dayData.PunchSets.forEach((punchSet) => {
				if (!punchSet.PunchOutId) missingPunchCount++
			})
		})
		return missingPunchCount
	}

	onClickEmployeeImgCol(e, userId: string) {
		e.stopPropagation()
		if (!permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.ViewEmployees)) return
		api.checkUserDeleted(userId).then(res =>
			res.data ?
				AppStateStore.setProgressSnackbarContent(strings.This_employee_has_been_deleted) :
				browserHistory.push("/user-details/" + userId + "/timecards")
		)
	}

	render() {
		return this.props.expandState &&
			<Table style={{ backgroundColor: "none" }} selectable multiSelectable onRowSelection={this.handleSelectEmployee}>
				<TableHeader displaySelectAll={false}>
					<TableRow className="ShortSelectBoxTableHeader">
						<TableHeaderColumn style={styles.imageCol} />
						<TableHeaderColumn className="TextOverflow" title={strings.Employee} style={styles.employeeCol}>{strings.Employee}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.TotalHours} style={styles.currencyCol}>{strings.TotalHours}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Total_Paid} style={styles.currencyCol}>{strings.Total_Paid}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Total_Deducted} style={styles.currencyCol}>{strings.Total_Deducted}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Taxable} style={styles.currencyCol}>{strings.Taxable}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Non_Taxable} style={styles.currencyCol}>{strings.Non_Taxable}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.EMP_Approved} style={styles.empApprovedCol}>{strings.EMP_Approved}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Approved} style={styles.approvedCol}>{strings.Approved}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Locked} style={styles.exportedCol}>{strings.Locked}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Exported} style={styles.exportedCol}>{strings.Exported}</TableHeaderColumn>
					</TableRow>
				</TableHeader>
				<TableBody deselectOnClickaway={false}>
					{this.props.employees.map((employeeData, index) => {
						const selected = this.state.selectedEmployees.find((item) => item.EmployeeId === employeeData.EmployeeId) ? true : false
						const individualLockedData: ILockedObjData = this.props.payrollHierarchyLockedData &&
							this.props.payrollHierarchyLockedData.LockedObjData &&
							this.props.payrollHierarchyLockedData.LockedObjData.find(i => i.LockedObjId === employeeData.EmployeeId)
						return (
							<TableRow key={index} className="ShortSelectBoxTableRow" selected={selected}>
								<TableRowColumn style={{ paddingLeft: 0, paddingRight: 0 }}>
									<Table
										style={{ backgroundColor: "none" }}
										onCellClick={() => this.setState({ expandRow: index === this.state.expandRow ? -1 : index })}
									>
										<TableBody displayRowCheckbox={false}>
											<TableRow data-intercom-target={index === 0 && "menu-Payroll-PayrollExpensesHierarchyTable-openEmployeeRowDetails"} style={{ cursor: "pointer", height: 48, borderBottom: index === this.state.expandRow ? "solid 1px rgb(204, 204, 204)" : "none" }}>
												<TableRowColumn style={styles.imageCol}>
													{employeeData.ProfileImageS3Url &&
														<div
															onClick={e => this.onClickEmployeeImgCol(e, employeeData.EmployeeId)}
															style={{ cursor: "pointer" }}
														>
															<ImageLoader url={employeeData.ProfileImageS3Url} fixedHeight={75} avatar={true} />
														</div>
													}
												</TableRowColumn>
												<TableRowColumn style={styles.employeeCol}>{`${employeeData.FirstName} ${employeeData.LastName}`}
													<br></br>
													<span style={{ color: "rgb(158, 158, 158)", fontSize: 10 }}>Emp #: {employeeData.ExternalId}</span>
												</TableRowColumn>
												<TableRowColumn style={styles.currencyCol}>{employeeData.ExpensesTotal.TotalTime}</TableRowColumn>
												<TableRowColumn style={styles.currencyCol}>{utils.getCurrencyFormat(employeeData.ExpensesTotal.TotalPaid)}</TableRowColumn>
												<TableRowColumn style={styles.currencyCol}>{utils.getCurrencyFormat(employeeData.ExpensesTotal.TotalDeducted)}</TableRowColumn>
												<TableRowColumn style={styles.currencyCol}>{utils.getCurrencyFormat(employeeData.ExpensesTotal.TotalTaxable)}</TableRowColumn>
												<TableRowColumn style={styles.currencyCol}>{utils.getCurrencyFormat(employeeData.ExpensesTotal.TotalNonTaxable)}</TableRowColumn>
												<TableRowColumn style={styles.empApprovedCol}>
													{employeeData.AllExpensesEmpApproved &&
														<ExpensesApproveIconTooltip
															identify={`Expenses_EmployeeRowEmpApprove${index}`}
															toolTipPosition="bottom"
															toolTipArrow="center"
															approvalType={EApproveType.EmployeeApproved}
															width={14}
															expenses={employeeData.ExpensesPayrollDataList}
														/>
													}
												</TableRowColumn>
												<TableRowColumn style={styles.approvedCol}>
													{employeeData.AllExpensesApproved &&
														<ExpensesApproveIconTooltip
															identify={`Expenses_EmployeeRowApprove${index}`}
															toolTipPosition="bottom"
															toolTipArrow="center"
															approvalType={EApproveType.SupervisorApproved}
															width={18}
															expenses={employeeData.ExpensesPayrollDataList}
														/>
													}
												</TableRowColumn>
												<TableRowColumn style={styles.exportedCol}>
													{individualLockedData &&
														<PayrollByHierarchyTableLockedColumn
															keyStr={`payrollexpensehierarchyemployee${index}`}
															lockedData={individualLockedData}
														/>
													}
												</TableRowColumn>
												<TableRowColumn style={styles.exportedCol}>
													{!this.props.showExported && employeeData.ExportedBy &&
														<PayrollByHierarchyEmployeeRowExportedColumn
															keyStr={`expenses${index}`}
															exported={employeeData.Exported}
															exportNote={employeeData.ExportNote}
															exportedDate={employeeData.ExportedDate}
															exportedBy={employeeData.ExportedBy}
														/>
													}

													<span>
														{!this.props.showExported && employeeData.ExportedBy &&
															<PayrollByHierarchyEmployeeRowExportedColumn
																keyStr={`expenses${index}`}
																exported={employeeData.Exported}
																exportNote={employeeData.ExportNote}
																exportedDate={employeeData.ExportedDate}
																exportedBy={employeeData.ExportedBy}
															/>
														}
														{this.props.showExported &&
															<span>{employeeData.TotalExpensesPendingExport} Pending</span>
														}
													</span>
												</TableRowColumn>
											</TableRow>
											{index === this.state.expandRow &&
												<TableRow>
													<TableRowColumn
														colSpan={10}
														style={{ paddingLeft: 20, paddingBottom: 40, paddingTop: 40, paddingRight: 0 }}
													>
														<div onClick={(e) => e.stopPropagation()}>
															<UserDetailExpensesTable
																showExported={this.props.showExported}
																readonly
																hideCheckBox
																expenses={employeeData.ExpensesPayrollDataList}
																onExpensePayrollFileDownload={this.props.onExpensePayrollFileDownload}
															/>
														</div>
													</TableRowColumn>
												</TableRow>
											}
										</TableBody>
									</Table>
								</TableRowColumn>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>
	}
}

const styles = {
	imageCol: {
		width: 45,
		textAlign: "center",
		paddingLeft: 5,
		paddingRight: 5
	},
	approvedCol: {
		width: 50,
		textAlign: "center",
		paddingLeft: 10,
		paddingRight: 10
	},
	empApprovedCol: {
		width: 80,
		textAlign: "center",
		paddingLeft: 10,
		paddingRight: 10
	},
	employeeCol: {
		width: 150,
		paddingLeft: 10,
		paddingRight: 10
	},
	exportedCol: {
		width: 150,
		textAlign: "center",
		paddingLeft: 10,
		paddingRight: 10
	},
	missingPunchedCol: {
		width: 80,
		paddingLeft: 10,
		paddingRight: 10
	},
	currencyCol: {
		paddingLeft: 10,
		paddingRight: 10,
	},
}
